/**
 * discovery_client.ts — the client half of DISCOVERY THAT MAKES WORK (space campaign slice F, 2026-09-30).
 *
 * Reads what the server lets THIS pilot see (survey views, the lease, the desks, tenders, the log, licences, the
 * mystery's clues — row filters keep everyone else's rows on the server) and makes the calls. It decides nothing:
 * the server meters every second of work (≤ 1 Hz beats, ≤ 2 s credit), the client only keeps the beam on.
 * SpaceModule asks it four things — is this site shared stock, what should the beam do here, which extra beacons
 * stand in this system (the mystery's places, from MY clue rows), and the scanner cooldown here.
 */
import { atlas } from '../state.js';
import {
  scanCooldownS, MYSTERY_KEY, MYSTERY_RELAY_KEY, MYSTERY_WRECK_KEY, SITE_LABEL, type SiteKind, hazardBand,
  RESEARCH_POSTS, type RelayChoice, STABILIZE_UNITS, RESTORE_UNITS,
} from '@shared/space_survey.js';
import { SCAN_COOLDOWN_S } from '@shared/space_industry.js';
import { showToast } from './toast.js';

type Conn = NonNullable<typeof atlas.conn>;
type Db = Conn['db'];
export type ViewRow = ReturnType<Db['surveyView']['viewId']['find']> & object;
export type LeaseRow = ReturnType<Db['surveyLease']['leaseId']['find']> & object;
export type TenderRow = ReturnType<Db['researchTender']['tenderId']['find']> & object;
export type PostRow = ReturnType<Db['researchPost']['stationKey']['find']> & object;
export type ClueRow = ReturnType<Db['mysteryClue']['clueId']['find']> & object;
export type MysteryRow = ReturnType<Db['mysteryState']['mysteryKey']['find']> & object;

export const SURVEY_SUBS = [
  'SELECT * FROM survey_view', 'SELECT * FROM survey_lease', 'SELECT * FROM research_post', 'SELECT * FROM research_tender',
  'SELECT * FROM expedition_log', 'SELECT * FROM expedition_profile', 'SELECT * FROM expedition_public',
  'SELECT * FROM intel_offer', 'SELECT * FROM intel_licence', 'SELECT * FROM mystery_clue', 'SELECT * FROM mystery_state',
];

export type BeamWork = { mode: 'assess' | 'stabilize' | 'extract' | 'observe' | 'recover' | 'restore'; label: string } | { mode: null; label: string };
export type ExtraBeacon = { key: string; x: number; y: number; z: number; label: string };

const tsMs = (t: { microsSinceUnixEpoch: bigint } | undefined | null): number => (t ? Number(t.microsSinceUnixEpoch / 1000n) : 0);

class DiscoveryClient {
  private subConn: Conn | null = null;
  /** The step the pilot chose for their lease (the panel's radio); extract by default. */
  leaseMode: 'stabilize' | 'extract' = 'extract';
  /** The restoration's choice (the panel's radio). */
  restoreChoice: RelayChoice = 'public';
  private beatBusy = false;
  /** The lease whose row last showed a stabilize step (its completion hands the beam to extraction). */
  private stabilizingLease: bigint | null = null;
  private lastBeatAt = 0;
  /** Counters for levers / probes. */
  readonly stats = { beats: 0, refused: 0, lastError: '', lastOk: '' };

  get conn(): Conn | null { return atlas.conn ?? null; }

  /** Subscribe once per connection (idempotent; called every tick from the scan path and when the desk opens). */
  ensure(): void {
    const c = this.conn;
    if (!c || this.subConn === c) return;
    this.subConn = c;
    try { c.subscriptionBuilder().subscribe(SURVEY_SUBS); } catch { /* pre-publish */ }
  }

  private me() { return this.conn?.identity ?? null; }
  views(): ViewRow[] {
    const c = this.conn, me = this.me();
    if (!c || !me) return [];
    try { return [...c.db.surveyView.iter()].filter((v) => v.identity.isEqual(me)) as ViewRow[]; } catch { return []; }
  }
  view(sigKey: string): ViewRow | null { return this.views().find((v) => v.sigKey === sigKey) ?? null; }
  /** A generation seeded under discovery_jobs_v1 — the server gave me a view of it. */
  isShared(sigKey: string): boolean { const v = this.view(sigKey); return !!v && v.kind !== 'mystery'; }
  /** A shared site I should no longer show a beacon for (taken, collapsed, faded). */
  siteGone(sigKey: string): boolean { const v = this.view(sigKey); return !!v && (v.state === 'depleted' || v.state === 'collapsed' || v.state === 'expired'); }
  lease(): LeaseRow | null {
    const c = this.conn, me = this.me();
    if (!c || !me) return null;
    try { return ([...c.db.surveyLease.iter()].find((l) => l.claimant.isEqual(me) && l.state === 'active') as LeaseRow) ?? null; } catch { return null; }
  }
  posts(): PostRow[] { try { return [...(this.conn?.db.researchPost.iter() ?? [])] as PostRow[]; } catch { return []; } }
  post(stationKey: string): PostRow | null { return this.posts().find((p) => p.stationKey === stationKey) ?? null; }
  tenders(): TenderRow[] { try { return [...(this.conn?.db.researchTender.iter() ?? [])] as TenderRow[]; } catch { return []; } }
  clues(): ClueRow[] {
    const c = this.conn, me = this.me();
    if (!c || !me) return [];
    try { return ([...c.db.mysteryClue.iter()].filter((x) => x.identity.isEqual(me) && x.mysteryKey === MYSTERY_KEY) as ClueRow[]).sort((a, b) => a.step - b.step); } catch { return []; }
  }
  hasClue(step: number): boolean { return this.clues().some((c) => c.step === step); }
  mystery(): MysteryRow | null { try { return (this.conn?.db.mysteryState.mysteryKey.find(MYSTERY_KEY) as MysteryRow) ?? null; } catch { return null; } }
  /** The scanner cooldown in this system (the restored relay's public aid shortens it in the Commons Reach). */
  scanCooldown(systemId: string): number { return scanCooldownS(systemId, this.mystery()?.stage === 'restored' ? this.mystery()!.choice : '', SCAN_COOLDOWN_S); }

  /** The mystery's places in this system, from MY clue rows only (THE HIDDEN-POSITION LAW: nobody else's rows). */
  extraBeacons(systemId: string): ExtraBeacon[] {
    if (systemId !== 'sys0') return [];
    const cl = this.clues();
    const has = (n: number) => cl.some((c) => c.step === n);
    const c1 = cl.find((c) => c.step === 1), c2 = cl.find((c) => c.step === 2), c3 = cl.find((c) => c.step === 3);
    if (c3) return this.mystery()?.stage === 'restored' ? [] : [{ key: MYSTERY_RELAY_KEY, x: c3.nextX, y: c3.nextY, z: c3.nextZ, label: 'the Fenmark relay (restore it)' }];
    if (c2 && !has(3)) return [{ key: MYSTERY_WRECK_KEY, x: c2.nextX, y: c2.nextY, z: c2.nextZ, label: "Ninth Lantern's wreck (its recorder)" }];
    if (c1 && !has(2)) return [{ key: MYSTERY_RELAY_KEY, x: c1.nextX, y: c1.nextY, z: c1.nextZ, label: 'the Fenmark relay (look closely)' }];
    return [];
  }
  /** True when the beam on this key must beat surveyWork (a shared site or a mystery place). */
  handles(key: string): boolean { return key === MYSTERY_RELAY_KEY || key === MYSTERY_WRECK_KEY || this.isShared(key); }

  /** What the beam does at this key right now — or why it does nothing. */
  beamWork(key: string): BeamWork {
    if (key === MYSTERY_WRECK_KEY) return { mode: 'recover', label: "pulling Ninth Lantern's recorder" };
    if (key === MYSTERY_RELAY_KEY) {
      if (this.hasClue(3)) return { mode: 'restore', label: `restoring the Fenmark relay (${this.restoreChoice === 'public' ? 'public navigation aid' : 'private research access'}; ${RESTORE_UNITS} u crystal)` };
      return { mode: 'observe', label: 'observing the Fenmark relay' };
    }
    const v = this.view(key);
    if (!v) return { mode: null, label: '' };
    const kind = SITE_LABEL[v.kind as SiteKind] ?? v.kind;
    const l = this.lease();
    if (l && l.sigKey === key) {
      // A stabilization the server just finished (the row's step cleared) hands the beam to extraction: the choice was
      // ONE stabilization — never a second burn the pilot did not ask for, nor a refusal once the crystal is spent.
      if (this.leaseMode === 'stabilize' && this.stabilizingLease === l.leaseId && l.mode === '') {
        this.leaseMode = 'extract';
        this.stabilizingLease = null;
        showToast(`Stabilized — the ${kind} reads hazard ${v.hazard}; the beam extracts next`, 'success');
      }
      if (l.mode === 'stabilize') this.stabilizingLease = l.leaseId;
      return this.leaseMode === 'stabilize' ? { mode: 'stabilize', label: `stabilizing the ${kind} (burns ${STABILIZE_UNITS} u crystal)` } : { mode: 'extract', label: `extracting the ${kind}` };
    }
    if (v.stage !== 'assessed') return { mode: 'assess', label: `assessing the ${kind}` };
    if (v.state === 'leased') return { mode: null, label: `the ${kind} is leased by another pilot` };
    if (v.state !== 'available') return { mode: null, label: `the ${kind} is ${v.state}` };
    return { mode: null, label: `the ${kind} is assessed — lease it at the survey desk to extract` };
  }

  /** Progress of the step the beam is on (0..1) for the HUD. */
  progress(key: string): number {
    const l = this.lease();
    if (l && l.sigKey === key && l.needMs > 0) return Math.min(1, l.progressMs / l.needMs);
    const v = this.view(key);
    if (v && v.workNeedMs > 0) return Math.min(1, v.workMs / v.workNeedMs);
    return 0;
  }

  /** One heartbeat (≤ 1 Hz; the server credits ≤ 2 s and ignores anything faster). */
  beat(key: string, onResult: (ok: boolean, msg: string) => void): void {
    const c = this.conn;
    const w = this.beamWork(key);
    if (!c || !w.mode || this.beatBusy) return;
    const now = performance.now();
    if (now - this.lastBeatAt < 1_000) return;
    this.lastBeatAt = now;
    this.beatBusy = true;
    this.stats.beats++;
    (c.reducers as unknown as { surveyWork(a: { sigKey: string; mode: string; choice: string }): Promise<void> })
      .surveyWork({ sigKey: key, mode: w.mode, choice: w.mode === 'restore' ? this.restoreChoice : '' })
      .then(() => { this.beatBusy = false; this.stats.lastOk = w.mode ?? ''; onResult(true, w.label); })
      .catch((e: Error) => { this.beatBusy = false; this.stats.refused++; this.stats.lastError = String(e.message ?? e); onResult(false, this.stats.lastError); });
  }

  /** The HUD line for a shared site or a mystery place in beam reach. */
  hudLine(key: string, working: boolean): string {
    const w = this.beamWork(key);
    const pct = Math.round(this.progress(key) * 100);
    const v = this.view(key);
    const band = v && v.band ? ` · hazard ${v.stage === 'assessed' ? `${v.hazard} (${v.band})` : v.band}` : '';
    if (!w.mode) return `📡 ${w.label}${band}`;
    return working ? `📡 ${w.label.toUpperCase()} — ${pct}%${band} (the server meters the beam: keep it on)` : `📡 ${w.label} — hold the beam${band}`;
  }

  // ── calls (each returns the reducer's promise; the caller toasts) ──
  private r() { return this.conn?.reducers as unknown as Record<string, (a: Record<string, unknown>) => Promise<void>>; }
  leaseSite(sigKey: string, tenderId: bigint): Promise<void> { return this.r().leaseSite({ sigKey, tenderId }); }
  release(): Promise<void> { return this.r().releaseSiteLease({}); }
  accept(tenderId: bigint): Promise<void> { return this.r().acceptResearchTender({ tenderId }); }
  abandon(tenderId: bigint): Promise<void> { return this.r().abandonResearchTender({ tenderId }); }
  deliver(tenderId: bigint): Promise<void> { return this.r().deliverResearchTender({ tenderId }); }
  handIn(stationKey: string): Promise<void> { return this.r().handInRelay({ stationKey }); }
  fund(stationKey: string, amount: number): Promise<void> { return this.r().fundResearchPost({ stationKey, amount }); }
  census(stationKey: string): Promise<void> { return this.r().readSurveyCensus({ stationKey }); }
  offer(sigKey: string, scope: string, price: number): Promise<void> { return this.r().offerIntelLicence({ sigKey, scope, price }); }
  withdraw(offerId: bigint): Promise<void> { return this.r().withdrawIntelOffer({ offerId }); }
  buy(offerId: bigint): Promise<void> { return this.r().buyIntelLicence({ offerId }); }
  setPublic(isPublic: boolean): Promise<void> { return this.r().setExpeditionPublic({ isPublic }); }
  readRelayLog(): Promise<void> { return this.r().readRelayLog({}); }
}

export const discovery = new DiscoveryClient();

export const POST_NAMES: Record<string, string> = Object.fromEntries(RESEARCH_POSTS.map((p) => [p.key, p.name]));
export { hazardBand, tsMs };
